import type { Layers } from '$lib/fleetModes/types';
import { parse, type Viewport } from '$lib/viewport';
import type { FleetModeKey } from '../stores/fleet.svelte';
import { type Basemap, isBasemap, type Tab } from '../stores/ui.svelte';

export interface ViewState {
  tab: Tab;
  mode: FleetModeKey;
  basemap: Basemap;
  layers: Layers;
  viewport: Viewport | null;
  /** The open map popup. On the Fleet tab it's FleetPins' marker id (`u:<id>`,
   *  `c:<cabinet>`, `spiro:<name>`, `arc:<name>`, `solar:<site-id>`); on the Map tab it's the
   *  selected site as `site:<vid>`. Absent when nothing is open. */
  sel?: string | null;
  /** The open site detail modal, as a site vid/id. Independent of `sel`: the
   *  modal opens from a popup, so both are usually set together. */
  detail?: string | null;
}

const LAYER_FLAG_MAP: Record<keyof Layers, string> = {
  moving: 'm',
  parked: 'p',
  cabinets: 'c',
  solo: 's',
  spiro: 'S',
  arcride: 'A',
  solar: 'R',
};

const FLAG_TO_LAYER = Object.entries(LAYER_FLAG_MAP).reduce(
  (acc, [layer, flag]) => {
    acc[flag] = layer as keyof Layers;
    return acc;
  },
  {} as Record<string, keyof Layers>,
);

export function encodeLayers(layers: Layers): string {
  let flags = '';
  for (const [layer, flag] of Object.entries(LAYER_FLAG_MAP) as [keyof Layers, string][]) {
    if (layers[layer]) flags += flag;
  }
  return flags;
}

export function decodeLayers(flags: string): Partial<Layers> {
  const result: Partial<Layers> = {
    moving: false,
    parked: false,
    cabinets: false,
    solo: false,
    spiro: false,
    arcride: false,
    solar: false,
  };
  for (const char of flags) {
    const layer = FLAG_TO_LAYER[char];
    if (layer) {
      result[layer] = true;
    }
  }
  return result;
}

export function encode(s: ViewState): URLSearchParams {
  const q = new URLSearchParams();
  if (s.tab !== 'map') q.set('tab', s.tab);
  if (s.tab === 'fleet' && s.mode && s.mode !== 'density') q.set('mode', s.mode);
  if (s.basemap && s.basemap !== 'google-roadmap') q.set('basemap', s.basemap);

  const layerFlags = encodeLayers(s.layers);
  // Default layers: moving(m), parked(p), cabinets(c), solar(R) -> "mpcR"
  if (s.tab === 'fleet' && layerFlags !== 'mpcR') {
    q.set('layers', layerFlags);
  }

  if (s.sel) q.set('sel', s.sel);
  if (s.detail) q.set('detail', s.detail);

  if (s.viewport) {
    q.set('center', `${s.viewport.center[0]},${s.viewport.center[1]}`);
    q.set('zoom', `${s.viewport.zoom}`);
  }

  return q;
}

export function decode(q: URLSearchParams): Partial<ViewState> {
  const out: Partial<ViewState> = {};

  const tabParam = q.get('tab');
  if (tabParam === 'map' || tabParam === 'dashboard' || tabParam === 'fleet') {
    out.tab = tabParam;
  }

  const modeParam = q.get('mode');
  if (modeParam) out.mode = modeParam;

  const basemapParam = q.get('basemap');
  if (isBasemap(basemapParam)) {
    out.basemap = basemapParam;
  }

  const layersParam = q.get('layers');
  if (layersParam !== null) {
    out.layers = decodeLayers(layersParam) as Layers;
  }

  const selParam = q.get('sel');
  if (selParam) out.sel = selParam;

  const detailParam = q.get('detail');
  if (detailParam) out.detail = detailParam;

  const centerParam = q.get('center');
  const zoomParam = q.get('zoom');
  if (centerParam && zoomParam) {
    const [latStr, lngStr] = centerParam.split(',');
    const lat = Number.parseFloat(latStr);
    const lng = Number.parseFloat(lngStr);
    const zoom = Number.parseFloat(zoomParam);
    const vp = parse({ center: [lat, lng], zoom });
    if (vp) out.viewport = vp;
  }

  return out;
}
